import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Download, Upload, CheckCircle2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  descargarContratoPDF,
  descargarContratoFirmado,
  uploadContratoFirmado,
} from "@/features/config/api";
import { formatDate } from "@/lib/utils";
import { openBlobInNewTab } from "@/lib/pdf";
import { useQueryClient } from "@tanstack/react-query";
import type { ContratoDetail } from "./schemas";
import type { ApiError } from "@/lib/api";

type Props = { contrato: ContratoDetail };

/**
 * Sección "Contrato firmado" que aparece en el detalle del contrato.
 * Permite:
 *   - Descargar el PDF generado desde la plantilla (para imprimir y firmar).
 *   - Subir el PDF firmado escaneado (flow manual F18.1).
 *   - Re-descargar el firmado si ya existe.
 *   - Reemplazar el firmado si fue cambio.
 */
export function ContratoDocumentoSection({ contrato }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [downloading, setDownloading] = useState<"plain" | "firmado" | null>(null);
  const [uploading, setUploading] = useState(false);

  const hasFirmado = !!contrato.contrato_firmado_at;

  async function handleDescargar() {
    setDownloading("plain");
    try {
      const blob = await descargarContratoPDF(contrato.id);
      openBlobInNewTab(blob);
    } catch (e) {
      toast.error("Error al generar contrato", {
        description: (e as ApiError).message ?? "Ver logs del API",
      });
    } finally {
      setDownloading(null);
    }
  }

  async function handleVerFirmado() {
    setDownloading("firmado");
    try {
      const blob = await descargarContratoFirmado(contrato.id);
      openBlobInNewTab(blob);
    } catch (e) {
      toast.error("Error al descargar firmado", {
        description: (e as ApiError).message,
      });
    } finally {
      setDownloading(null);
    }
  }

  async function handleUpload(file: File) {
    if (file.type !== "application/pdf") {
      toast.error("El archivo debe ser PDF");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("El PDF supera 20 MB");
      return;
    }
    setUploading(true);
    try {
      await uploadContratoFirmado(contrato.id, file);
      toast.success("Contrato firmado guardado");
      qc.invalidateQueries({ queryKey: ["contratos"] });
    } catch (e) {
      toast.error("Error al subir", {
        description: (e as ApiError).message,
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="mb-3 flex items-center gap-2">
        <FileText size={14} />
        <h3 className="text-sm font-semibold">Contrato</h3>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm" variant="outline"
          disabled={downloading !== null}
          onClick={handleDescargar}
        >
          <Download size={12} />
          {downloading === "plain" ? "Generando…" : "Descargar contrato (para firmar)"}
        </Button>

        {hasFirmado ? (
          <>
            <Badge variant="success" className="inline-flex items-center gap-1">
              <CheckCircle2 size={10} />
              Firmado · {formatDate(contrato.contrato_firmado_at!)}
              {contrato.contrato_firma_metodo === "manual" && " (manual)"}
            </Badge>
            <Button
              size="sm" variant="ghost"
              disabled={downloading !== null}
              onClick={handleVerFirmado}
            >
              <Download size={12} />
              Ver firmado
            </Button>
            <Button
              size="sm" variant="ghost"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              title="Reemplazar PDF firmado"
            >
              <RefreshCw size={12} />
              Reemplazar
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={12} />
            {uploading ? "Subiendo…" : "Subir firmado"}
          </Button>
        )}
      </div>

      {!hasFirmado && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          1) Descarga el contrato. 2) Imprímelo. 3) Cliente firma. 4) Escanea/foto a PDF.
          5) Sube el PDF firmado aquí.
        </p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
        }}
      />
    </div>
  );
}
